import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import {
  Users,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  TrendingUp,
  Target,
  Timer,
  Briefcase,
  FileDown
} from 'lucide-react';

const formatHours = (h) => {
  if (!h || h === 0) return '-';
  if (h < 1) return `${Math.round(h * 60)}min`;
  if (h < 24) return `${h}h`;
  const days = Math.floor(h / 24);
  const rem = Math.round(h % 24);
  return rem > 0 ? `${days}g ${rem}h` : `${days}g`;
};

const TechnicianProductivityPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [hotelFilter, setHotelFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  const downloadCard = async (tech) => {
    try {
      setDownloadingId(tech.id);
      const response = await api.get(`/analytics/technician-card-pdf/${tech.id}?period=${period}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = (tech.name || 'tecnico').replace(/\s+/g, '_').toLowerCase();
      link.setAttribute('download', `scheda_tecnico_${safe}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Scheda ${tech.name} scaricata`);
    } catch (e) {
      // Quando responseType=blob, anche gli errori sono blob
      let detail = 'Errore durante il download';
      try {
        if (e.response?.data instanceof Blob) {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        } else if (e.response?.data?.detail) {
          detail = e.response.data.detail;
        }
      } catch (_) { /* keep default */ }
      toast.error(detail);
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ period });
      if (hotelFilter !== 'all') params.append('hotel_id', hotelFilter);

      const [prodRes, hotelsRes] = await Promise.all([
        api.get(`/analytics/technician-productivity?${params}`),
        api.get('/hotels')
      ]);
      setData(prodRes.data);
      setHotels(hotelsRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period, hotelFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  const techs = data?.technicians || [];

  // Summary KPIs
  const totalAssigned = techs.reduce((s, t) => s + t.assigned, 0);
  const totalCompleted = techs.reduce((s, t) => s + t.completed, 0);
  const totalOpen = techs.reduce((s, t) => s + t.open, 0);
  const avgRate = techs.length > 0
    ? Math.round(techs.reduce((s, t) => s + t.closure_rate, 0) / techs.length)
    : 0;

  // Chart data for bar comparison
  const chartData = techs.map(t => ({
    name: t.name.split(' ')[0],
    completati: t.completed,
    aperti: t.open,
    tasso: t.closure_rate
  }));

  // Resolution time chart
  const resolutionChart = techs
    .filter(t => t.avg_resolution_hours > 0)
    .map(t => ({
      name: t.name.split(' ')[0],
      media: t.avg_resolution_hours,
      min: t.min_resolution_hours,
      max: t.max_resolution_hours
    }));

  const COLORS = ['#1e3a5f', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'];

  return (
    <div className="space-y-6" data-testid="technician-productivity-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">Produttività Tecnici</h1>
          <p className="text-slate-500 mt-1">Performance individuale dei manutentori</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={hotelFilter} onValueChange={setHotelFilter}>
            <SelectTrigger className="w-48" data-testid="hotel-filter">
              <SelectValue placeholder="Tutti gli hotel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli hotel</SelectItem>
              {hotels.map(h => (
                <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 giorni</SelectItem>
              <SelectItem value="30">30 giorni</SelectItem>
              <SelectItem value="90">90 giorni</SelectItem>
              <SelectItem value="180">180 giorni</SelectItem>
              <SelectItem value="365">365 giorni</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Assegnati</p>
              <p className="text-2xl font-black text-slate-900">{totalAssigned}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Completati</p>
              <p className="text-2xl font-black text-green-700">{totalCompleted}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">In Carico</p>
              <p className="text-2xl font-black text-amber-700">{totalOpen}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Target className="w-5 h-5 text-purple-700" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase font-medium">Tasso Medio</p>
              <p className="text-2xl font-black text-purple-700">{avgRate}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Completati vs Aperti */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              Ticket per Tecnico
            </CardTitle>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(value, name) => [value, name === 'completati' ? 'Completati' : 'Aperti']}
                  />
                  <Legend formatter={(v) => v === 'completati' ? 'Completati' : 'In Carico'} />
                  <Bar dataKey="completati" name="completati" fill="#16a34a" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="aperti" name="aperti" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 py-12">Nessun dato nel periodo</p>
            )}
          </CardContent>
        </Card>

        {/* Tempo medio risoluzione */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base font-bold flex items-center gap-2">
              <Timer className="w-4 h-4 text-cyan-600" />
              Tempo Medio Risoluzione (ore)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {resolutionChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={resolutionChart} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} unit="h" />
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0' }}
                    formatter={(value) => [`${value}h`]}
                  />
                  <Bar dataKey="media" name="Media" radius={[4, 4, 0, 0]}>
                    {resolutionChart.map((_, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-slate-500 py-12">Nessun dato nel periodo</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-600" />
            Dettaglio Tecnici — Ultimi {period} giorni
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">Clicca su un tecnico per vedere tutti i suoi ticket. Usa il pulsante <b>PDF</b> per scaricare la scheda performance completa.</p>
        </CardHeader>
        <CardContent>
          <ScrollArea className="w-full">
            <div className="min-w-[700px]">
              <table className="w-full text-sm" data-testid="productivity-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-3 font-semibold text-slate-600">#</th>
                    <th className="text-left py-3 px-3 font-semibold text-slate-600">Tecnico</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Assegnati</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Completati</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">In Carico</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Tasso Chiusura</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Tempo Medio</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Min / Max</th>
                    <th className="text-left py-3 px-3 font-semibold text-slate-600">Ticket più lungo</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Urgenti</th>
                    <th className="text-center py-3 px-3 font-semibold text-slate-600">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {techs.map((tech, idx) => {
                    const rateColor = tech.closure_rate >= 80 ? 'text-green-700 bg-green-50' :
                                      tech.closure_rate >= 50 ? 'text-amber-700 bg-amber-50' :
                                      'text-red-700 bg-red-50';
                    return (
                      <tr
                        key={tech.id}
                        className="border-b border-slate-100 hover:bg-blue-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/tickets?assigned_to=${tech.id}`)}
                        title="Apri i ticket di questo tecnico"
                        data-testid={`tech-row-${idx}`}
                      >
                        <td className="py-3 px-3">
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs ${
                            idx === 0 ? 'bg-amber-100 text-amber-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>{idx + 1}</div>
                        </td>
                        <td className="py-3 px-3">
                          <p className="font-semibold text-slate-900">{tech.name}</p>
                          <p className="text-xs text-slate-400">{tech.email}</p>
                        </td>
                        <td className="py-3 px-3 text-center font-medium text-slate-700">{tech.assigned}</td>
                        <td className="py-3 px-3 text-center font-bold text-green-700">{tech.completed}</td>
                        <td className="py-3 px-3 text-center">
                          {tech.open > 0 ? (
                            <Badge variant="secondary" className="bg-amber-50 text-amber-700 font-bold">{tech.open}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold ${rateColor}`}>
                            {tech.closure_rate}%
                          </span>
                        </td>
                        <td className="py-3 px-3 text-center font-medium text-slate-700">
                          {formatHours(tech.avg_resolution_hours)}
                        </td>
                        <td className="py-3 px-3 text-center text-xs text-slate-500">
                          {tech.avg_resolution_hours > 0
                            ? `${formatHours(tech.min_resolution_hours)} / ${formatHours(tech.max_resolution_hours)}`
                            : '-'}
                        </td>
                        <td className="py-3 px-3 text-left">
                          {tech.longest_ticket ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${tech.longest_ticket.id}`); }}
                              className="inline-flex flex-col items-start text-left hover:text-blue-700 transition-colors group"
                              title={`Apri ticket: ${tech.longest_ticket.title}`}
                              data-testid={`longest-ticket-${idx}`}
                            >
                              <span className="text-xs font-semibold text-slate-900 group-hover:text-blue-700 max-w-[180px] truncate">
                                {tech.longest_ticket.title}
                              </span>
                              <span className="text-[10px] text-slate-500">
                                {tech.longest_ticket.area} · <span className="font-bold text-amber-700">{formatHours(tech.longest_ticket.hours)}</span>
                              </span>
                            </button>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          {tech.priority_breakdown.urgente > 0 ? (
                            <Badge className="bg-red-100 text-red-700 font-bold">{tech.priority_breakdown.urgente}</Badge>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); downloadCard(tech); }}
                            disabled={downloadingId === tech.id}
                            className="h-8 px-2.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                            title={`Scarica scheda PDF di ${tech.name}`}
                            data-testid={`download-card-${idx}`}
                          >
                            {downloadingId === tech.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <><FileDown className="w-3.5 h-3.5 mr-1" /> PDF</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {techs.length === 0 && (
                    <tr>
                      <td colSpan={11} className="py-12 text-center text-slate-500">
                        Nessun tecnico trovato per il periodo selezionato
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default TechnicianProductivityPage;
